// The composed Music-tab read (music) + the disown/restore curation — ONE band-scoped query
// that feeds every Music module (recent release / streaming / radio / charts). Hand-written over the generated
// client via the tenant-keyed wrapper: keyed ['band', bandId, 'music-stats'] so a band switch scorches
// it AND the `sync-completed` plane-B signal invalidates it live. Served straight from the analytics.*
// projections — NO vendor call on this read path; the numbers are the SAME per-artist projections the
// dashboard summary reads. The disown/restore mutations invalidate the same key so the table repaints.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

export type MusicView = components["schemas"]["MusicView"];
export type MusicStreamingBlock = components["schemas"]["MusicStreamingBlock"];
export type MusicRadioBlock = components["schemas"]["MusicRadioBlock"];
export type MusicChartsBlock = components["schemas"]["MusicChartsBlock"];
export type TrackStatView = components["schemas"]["TrackStatView"];

/** GET the composed Music tab (releases:read). `staleTime` ~60s per the stats domain.*/
export function useMusicStats() {
  const { api } = useApp();
  return useBandQuery<MusicView>(
    ["music-stats"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/music", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { staleTime: 60_000 },
  );
}

/** Disown a false-positive track by its Spotify id — idempotent PUT; invalidates the Music read so the
 * top-tracks table repaints without it. The success/error toast is pushed at the call site (it knows the title). */
export function useDisownTrack() {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<void, string>(
    async (band, spotifyTrackId) => {
      const { error, response } = await api.PUT("/api/v1/bands/{bandId}/music/disowned-tracks/{spotifyTrackId}", {
        params: { path: { bandId: band, spotifyTrackId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "music-stats"] }) },
  );
}

/** Restore a previously-disowned track (the disown "Undo") — idempotent DELETE; invalidates the Music read. */
export function useRestoreTrack() {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<void, string>(
    async (band, spotifyTrackId) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/music/disowned-tracks/{spotifyTrackId}", {
        params: { path: { bandId: band, spotifyTrackId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "music-stats"] }) },
  );
}
